import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OnboardingLayout from '../../components/onboarding/OnboardingLayout';
import OptionButton from '../../components/onboarding/OptionButton';
import { useOnboarding } from '../../contexts/OnboardingContext';

const OnboardingStep10 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { responses, updateResponses, markStepCompleted } = useOnboarding();
  const [selected, setSelected] = useState<string[]>(responses.infrequentExpenses || []);
  const progress = (10 / 14) * 100;

  // Determine conditional options based on previous responses
  const options = useMemo(() => {
    const baseOptions = [
      { value: 'medical_checkup', label: t('onboarding.step10.options.medical_checkup'), emoji: '🩺' },
      { value: 'perawatan_gigi', label: t('onboarding.step10.options.perawatan_gigi'), emoji: '🦷' },
      { value: 'pajak_biaya_lain', label: t('onboarding.step10.options.pajak_biaya_lain'), emoji: '💸' },
    ];

    const conditionalOptions = [];

    // Add if credit card selected in Step 5
    if (responses.loans?.includes('kartu_kredit')) {
      conditionalOptions.push({
        value: 'iuran_kartu_kredit',
        label: t('onboarding.step10.options.iuran_kartu_kredit'),
        emoji: '💳'
      });
    }

    // Add if Mobil selected in Step 6
    if (responses.transportation?.includes('mobil')) {
      conditionalOptions.push(
        { value: 'pajak_mobil', label: t('onboarding.step10.options.pajak_mobil'), emoji: '🚙' },
        { value: 'maintenance_mobil', label: t('onboarding.step10.options.maintenance_mobil'), emoji: '🔧' }
      );
    }

    // Add if Motor selected in Step 6
    if (responses.transportation?.includes('motor')) {
      conditionalOptions.push(
        { value: 'pajak_motor', label: t('onboarding.step10.options.pajak_motor'), emoji: '🏍️' },
        { value: 'maintenance_motor', label: t('onboarding.step10.options.maintenance_motor'), emoji: '⚙️' }
      );
    }

    // Add if own house selected in Step 4
    if (responses.housingType === 'own') {
      conditionalOptions.push({
        value: 'maintenance_rumah',
        label: t('onboarding.step10.options.maintenance_rumah'),
        emoji: '🛠️'
      });
    }

    return [...conditionalOptions, ...baseOptions];
  }, [responses, t]);

  const toggleOption = (value: string) => {
    setSelected(prev =>
      prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  const handleNext = () => {
    updateResponses('infrequentExpenses', selected);
    markStepCompleted(10);
    navigate('/onboarding/step-11');
  };

  const handleSkip = () => {
    setSelected([]);
    updateResponses('infrequentExpenses', []);
    navigate('/onboarding/step-11');
  };

  return (
    <OnboardingLayout progress={progress}>
      <div className="bg-white border-4 border-[#077f5d] p-8" style={{ boxShadow: '6px 6px 0 #077f5d' }}>
        <div className="mb-6">
          <h2 className="text-base text-center text-[#077f5d] mb-4 uppercase" style={{ fontFamily: "'Press Start 2P', monospace" }}>
            {t('onboarding.step10.title')}
          </h2>
        </div>

        <div className="space-y-3 mb-6">
          {options.map(option => (
            <OptionButton
              key={option.value}
              label={option.label}
              emoji={option.emoji}
              selected={selected.includes(option.value)}
              onClick={() => toggleOption(option.value)}
            />
          ))}
        </div>

        {/* Skip option as clickable text */}
        <div className="text-center mb-6">
          <button
            type="button"
            onClick={handleSkip}
            className="text-[#1a1a1a] font-bold opacity-70 hover:opacity-100 hover:underline transition-all duration-150"
            style={{
              fontSize: '14px',
            }}
          >
            {t('onboarding.step10.skip')}
          </button>
        </div>

        <button
          type="button"
          onClick={handleNext}
          disabled={selected.length === 0}
          className="w-full bg-[#3cbe8c] text-white px-8 py-4 border-2 border-[#077f5d] font-bold uppercase   hover:-translate-y-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-75 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0"
          style={{
            fontFamily: "'Press Start 2P', monospace",
            fontSize: '12px',
            boxShadow: '6px 6px 0 #077f5d'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
            e.currentTarget.style.transform = 'translate(-2px, -2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
            e.currentTarget.style.transform = 'translate(0, 0)';
          }}
        >
          {t('onboarding.step10.next')}
        </button>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingStep10;
